import { useState, useCallback, useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isBefore,
  isAfter,
  startOfDay,
} from 'date-fns';
import { CalendarView } from './types';

interface UseCalendarNavigationOptions {
  initialDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  view?: CalendarView;
}

interface UseCalendarNavigationReturn {
  currentDate: Date;
  view: CalendarView;
  goToPrevious: () => void;
  goToNext: () => void;
  goToToday: () => void;
  goToDate: (date: Date) => void;
  setView: (view: CalendarView) => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  monthStart: Date;
  monthEnd: Date;
}

export function useCalendarNavigation({
  initialDate = new Date(),
  minDate,
  maxDate,
  view: initialView = 'month',
}: UseCalendarNavigationOptions = {}): UseCalendarNavigationReturn {
  const [currentDate, setCurrentDate] = useState<Date>(startOfDay(initialDate));
  const [view, setView] = useState<CalendarView>(initialView);

  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);

  const canGoPrevious = useMemo(() => {
    if (!minDate) return true;
    const previousMonth = subMonths(currentDate, 1);
    return !isBefore(endOfMonth(previousMonth), startOfDay(minDate));
  }, [currentDate, minDate]);

  const canGoNext = useMemo(() => {
    if (!maxDate) return true;
    const nextMonth = addMonths(currentDate, 1);
    return !isAfter(startOfMonth(nextMonth), startOfDay(maxDate));
  }, [currentDate, maxDate]);

  const goToPrevious = useCallback(() => {
    if (!canGoPrevious) return;
    setCurrentDate((prev) => subMonths(prev, 1));
  }, [canGoPrevious]);

  const goToNext = useCallback(() => {
    if (!canGoNext) return;
    setCurrentDate((prev) => addMonths(prev, 1));
  }, [canGoNext]);

  const goToToday = useCallback(() => {
    const today = startOfDay(new Date());
    // Only go to today if it's within the allowed range
    if (minDate && isBefore(today, startOfDay(minDate))) {
      setCurrentDate(startOfDay(minDate));
      return;
    }
    if (maxDate && isAfter(today, startOfDay(maxDate))) {
      setCurrentDate(startOfDay(maxDate));
      return;
    }
    setCurrentDate(today);
  }, [minDate, maxDate]);

  const goToDate = useCallback(
    (date: Date) => {
      const targetDate = startOfDay(date);
      // Ensure the date is within bounds
      if (minDate && isBefore(targetDate, startOfDay(minDate))) return;
      if (maxDate && isAfter(targetDate, startOfDay(maxDate))) return;
      setCurrentDate(targetDate);
    },
    [minDate, maxDate]
  );

  return {
    currentDate,
    view,
    goToPrevious,
    goToNext,
    goToToday,
    goToDate,
    setView,
    canGoPrevious,
    canGoNext,
    monthStart,
    monthEnd,
  };
}
